import React from "react";
// components
import CustomHead from "src/components/ui/CustomHead";
import Layout from "src/layout";
import ClinicDetails from "src/components/containers/clinic-details";
// hooks
import { useClinics } from "src/hooks/services";
import { useReviews } from "src/hooks/services/useReviews";
import { useDynamicCommonData } from "src/hooks/dynamic/common";
import useAllPage from "src/hooks/dynamic/allPage";
import { useWindowSize } from "src/hooks";
// utils
import { calcAvgRating } from "src/utils/reviews";
// const
import { sizesMedia } from "src/constants/media";
// types
import { EVariants } from "src/constants/variants";
import { IReview, IReviewStatistic, TClinicDetails } from "src/types";
import { GetServerSideProps, NextPage } from "next";

interface IClinicPage {
  clinic: TClinicDetails;
  reviews: IReview[];
  reviewsCount: number;
  reviewsStatistic: IReviewStatistic;
}

const makeDescription = (clinic: TClinicDetails) => {
  const city = clinic.geo_data?.city;
  return `Leave a review for your vet ${
    city ? `in ${city}` : ""
  }, request appointments and find opening times for ${clinic.name} on PetsApp`;
};

const ClinicPage: NextPage<IClinicPage> = ({ clinic, reviews, reviewsCount, reviewsStatistic }) => {
  const size = useWindowSize();
  const avgRating = calcAvgRating(reviewsStatistic.rating);

  const widthHeader = size.width >= sizesMedia.laptopL ? { width: "1096px" } : { width: "100%" };

  const description = makeDescription(clinic);

  return (
    <>
      <CustomHead
        title={`PetsApp | ${clinic.name}`}
        og_title={`PetsApp | ${clinic.name}`}
        og_image={clinic.logo_url}
        description={description}
      />

      <Layout headerProps={{ variant: EVariants.SECONDARY, styles: widthHeader }}>
        <ClinicDetails
          clinic={clinic}
          reviews={reviews}
          reviewsCount={reviewsCount}
          avgRating={avgRating}
          reviewsStatistic={reviewsStatistic}
        />
      </Layout>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  ctx.res.setHeader("Cache-Control", "public, s-maxage=10");

  const { loadClinics } = useClinics();
  const { loadReviews, loadReviewsCount, loadReviewsStatistic } = useReviews();

  const clinicSlugString = (ctx.query.clinic_slug as string[]).join("/");
  const clinicSlugStringReview = clinicSlugString.startsWith("/")
    ? clinicSlugString
    : `/${clinicSlugString}`;

  const [clinics, reviews, reviewsCount, reviewsStatistic] = await Promise.all([
    loadClinics(`_limit=1&slug_contains=${clinicSlugString}&parent_null=1`),
    loadReviews(
      `_limit=3&_sort=created_at:desc&clinic.slug=${clinicSlugStringReview}&clinic.parent_null=1`,
    ),
    loadReviewsCount(`clinic.slug=${clinicSlugStringReview}&clinic.parent_null=1`),
    loadReviewsStatistic(`clinic=${clinicSlugStringReview}&clinic.parent_null=1`),
  ]);

  const initialReduxState = await useAllPage(ctx.locale as string);

  return {
    props: {
      initialReduxState: { DYNAMIC: initialReduxState.DYNAMIC },
      clinic: clinics[0],
      reviews,
      reviewsCount,
      reviewsStatistic,
    },
  };
};

export default ClinicPage;
